import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useBreadcrumb } from "../lib/BreadcrumbContext";
import {
  Tabs, Tab, Spinner, NonIdealState, Button, Tag, Card, Elevation, Callout,
  FormGroup, InputGroup, TextArea, HTMLSelect, Icon, Menu, ProgressBar,
  Dialog, DialogBody, DialogFooter, MenuItem, Collapse,
} from "@blueprintjs/core";
import { Suggest } from "@blueprintjs/select";
import {
  api,
  type Blueprint,
  type BlueprintMember,
  type BlueprintOverview,
  type Lead,
  type LeadListResponse,
  type LeadStatusBreakdown,
  type PipelineRun,
  type StrategyResponse,
  type ICP,
  type SourcePlan,
  type GatherPlan,
  type UserItem,
  type SampleReviewData,
  type SampleCompany,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";

const ACTION_LABELS: Record<string, { text: string; dot: string }> = {
  created: { text: "created", dot: "blue" },
  updated: { text: "updated", dot: "blue" },
  archived: { text: "archived", dot: "orange" },
  unarchived: { text: "restored", dot: "green" },
  member_added: { text: "added a member to", dot: "purple" },
  member_removed: { text: "removed a member from", dot: "orange" },
  pipeline_completed: { text: "completed pipeline for", dot: "green" },
  pipeline_started: { text: "started pipeline for", dot: "blue" },
  pipeline_failed: { text: "pipeline failed for", dot: "red" },
  lead_claimed: { text: "claimed a lead in", dot: "orange" },
  lead_updated: { text: "updated a lead in", dot: "blue" },
  status_changed: { text: "changed status of", dot: "blue" },
  note_added: { text: "added a note on", dot: "purple" },
  follow_up_created: { text: "scheduled follow-up for", dot: "blue" },
  follow_up_completed: { text: "completed follow-up for", dot: "green" },
};

const BP_VALID_TABS = new Set(["overview", "strategy", "info", "leads", "members"]);

export function BlueprintDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const { setPageTitle } = useBreadcrumb();

  const [bp, setBp] = useState<Blueprint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const tabParam = searchParams.get("tab") || "overview";
  const activeTab = BP_VALID_TABS.has(tabParam) ? tabParam : "overview";

  const setActiveTab = useCallback((tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchBp = useCallback(() => {
    if (!id) return;
    api.get<Blueprint>(`/blueprints/${id}`)
      .then(setBp)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchBp(); }, [fetchBp]);

  useEffect(() => {
    if (bp?.name) setPageTitle(bp.name);
    return () => setPageTitle("");
  }, [bp?.name, setPageTitle]);

  useEffect(() => {
    if (!bp) return;
    const isRunning = ["planning", "gathering", "enriching", "scoring", "sample_enriching"].includes(bp.status);
    if (!isRunning) return;
    const interval = setInterval(fetchBp, 3000);
    return () => clearInterval(interval);
  }, [bp?.status, fetchBp]);

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={32} /></div>;
  }
  if (error || !bp) {
    return (
      <NonIdealState
        icon="error"
        title="Blueprint not found"
        description={error}
        action={<Button onClick={() => navigate("/blueprints")}>Back to Blueprints</Button>}
      />
    );
  }

  const isOwnerOrAdmin = currentUser?.role === "superadmin" ||
    bp.members.some((m) => m.user_id === currentUser?.id && m.role === "owner");

  return (
    <div className="bp-detail-page">
      <div className="bp-detail-header">
        <div className="bp-detail-header-left">
          <Button icon="arrow-left" variant="minimal" onClick={() => navigate("/blueprints")} style={{ marginRight: 4 }} />
          <div>
            <div className="bp-detail-title-row">
              <h2 className="bp-detail-title">{bp.name}</h2>
              <StatusTag status={bp.status} />
              {bp.is_archived && <Tag intent="warning" minimal>Archived</Tag>}
            </div>
            <p className="bp-detail-meta">
              Created by {bp.creator_name ?? "Unknown"} · {fmtDate(bp.created_at)}
              {bp.members.length > 1 && ` · ${bp.members.length} members`}
            </p>
          </div>
        </div>
        <div className="bp-detail-header-actions">
          <PipelineActions bp={bp} onUpdate={fetchBp} onSwitchTab={setActiveTab} />
          {isOwnerOrAdmin && !bp.is_archived && (
            <Button icon="archive" variant="outlined" onClick={() => doArchive(id!, setBp)}>Archive</Button>
          )}
          {isOwnerOrAdmin && bp.is_archived && (
            <Button icon="unarchive" intent="primary" onClick={() => doUnarchive(id!, setBp)}>Unarchive</Button>
          )}
        </div>
      </div>

      <Tabs
        id="bp-tabs"
        selectedTabId={activeTab}
        onChange={(t) => setActiveTab(t as string)}
        large
        className="bp-detail-tabs"
      >
        <Tab id="overview" title="Overview" panel={<OverviewTab blueprintId={bp.id} />} />
        <Tab id="strategy" title="Strategy" panel={<StrategyTab bp={bp} onUpdate={fetchBp} />} />
        <Tab id="info" title="Info" panel={<InfoTab bp={bp} isOwner={isOwnerOrAdmin} onUpdate={fetchBp} />} />
        <Tab id="leads" title={`Leads (${bp.lead_count})`} panel={<LeadsTab blueprintId={bp.id} blueprintName={bp.name} leadCount={bp.lead_count} />} />
        <Tab id="members" title={`Members (${bp.members.length})`} panel={<MembersTab bp={bp} isOwner={isOwnerOrAdmin} onUpdate={fetchBp} />} />
      </Tabs>
    </div>
  );
}

// ── Helpers ──

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatusTag({ status }: { status: string }) {
  const map: Record<string, { intent: "success" | "primary" | "warning" | "danger" | "none"; label: string }> = {
    completed: { intent: "success", label: "Completed" },
    gathering: { intent: "primary", label: "Gathering" },
    enriching: { intent: "primary", label: "Enriching" },
    scoring: { intent: "primary", label: "Scoring" },
    planning: { intent: "warning", label: "Planning" },
    strategy_review: { intent: "warning", label: "Strategy Review" },
    sample_review: { intent: "warning", label: "Sample Review" },
    sample_enriching: { intent: "primary", label: "Enriching Sample" },
    draft: { intent: "none", label: "Draft" },
    failed_planning: { intent: "danger", label: "Failed" },
    failed_gathering: { intent: "danger", label: "Failed" },
    failed_enriching: { intent: "danger", label: "Failed" },
  };
  const c = map[status] ?? { intent: "none" as const, label: status };
  return <Tag minimal round intent={c.intent}>{c.label}</Tag>;
}

async function doArchive(id: string, setBp: (b: Blueprint) => void) {
  try { setBp(await api.patch<Blueprint>(`/blueprints/${id}/archive`)); } catch {}
}
async function doUnarchive(id: string, setBp: (b: Blueprint) => void) {
  try { setBp(await api.patch<Blueprint>(`/blueprints/${id}/unarchive`)); } catch {}
}

// ── Pipeline Header Actions ──

const RUNNING_LABELS: Record<string, string> = {
  planning: "Generating Strategy...",
  gathering: "Gathering Data...",
  sample_enriching: "Enriching Sample...",
  enriching: "AI Enrichment...",
  scoring: "Scoring Leads...",
};

function PipelineActions({ bp, onUpdate, onSwitchTab }: { bp: Blueprint; onUpdate: () => void; onSwitchTab?: (tab: string) => void }) {
  const [actionError, setActionError] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const actionInFlight = useRef(false);
  const [prevStatus, setPrevStatus] = useState(bp.status);

  if (bp.status !== prevStatus) {
    setPrevStatus(bp.status);
    if (actionError) setActionError("");
    if (pendingAction) setPendingAction(null);
    actionInFlight.current = false;
  }

  const runAction = async (action: () => Promise<void>, label: string) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setPendingAction(label);
    setActionError("");
    try {
      await action();
      onUpdate();
    } catch (e: any) {
      setActionError(e?.message || "Failed to start");
      setPendingAction(null);
      actionInFlight.current = false;
    }
  };

  const handleGenerateStrategy = () => runAction(
    () => api.post(`/blueprints/${bp.id}/generate-strategy`),
    "Generating Strategy...",
  );

  const handleExecute = () => runAction(
    () => api.post(`/blueprints/${bp.id}/execute`),
    "Starting Pipeline...",
  );

  const handleCancel = async () => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setPendingAction("Cancelling...");
    try {
      await api.post(`/blueprints/${bp.id}/cancel`);
      setActionError("");
      onUpdate();
    } catch (e: any) {
      setActionError(e?.message || "Failed to cancel");
    } finally {
      setPendingAction(null);
      actionInFlight.current = false;
    }
  };

  const s = bp.status;
  const isRunning = ["planning", "gathering", "enriching", "scoring", "sample_enriching"].includes(s);
  const isBusy = !!pendingAction || isRunning;
  const is409 = actionError.toLowerCase().includes("already running");

  const errorEl = actionError ? (
    <Callout intent="danger" icon="error" compact style={{ fontSize: 12, padding: "4px 10px", marginRight: 8 }}>
      {actionError}
      {is409 && (
        <Button size="small" intent="danger" variant="minimal" icon="cross"
          onClick={handleCancel} style={{ marginLeft: 8, fontSize: 11 }}>
          Cancel Stuck Run
        </Button>
      )}
    </Callout>
  ) : null;

  if (pendingAction && !isRunning) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {errorEl}
        <Spinner size={16} />
        <span style={{ fontSize: 13, fontWeight: 500, color: "#2b95d6" }}>
          {pendingAction}
        </span>
      </div>
    );
  }

  if (s === "draft" && bp.requirement_text) {
    return (
      <>
        {errorEl}
        <Button icon="rocket-slant" intent="primary" loading={isBusy} disabled={isBusy} onClick={handleGenerateStrategy}>Generate Strategy</Button>
      </>
    );
  }
  if (s === "strategy_review") {
    return (
      <>
        {errorEl}
        <Button icon="refresh" variant="outlined" loading={isBusy} disabled={isBusy} onClick={handleGenerateStrategy}>Regenerate</Button>
        <Button icon="play" intent="primary" loading={isBusy} disabled={isBusy} onClick={handleExecute}>Approve & Execute</Button>
      </>
    );
  }
  if (isRunning) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Spinner size={16} />
        <span style={{ fontSize: 13, fontWeight: 500, color: "#2b95d6" }}>
          {RUNNING_LABELS[s] || "Running..."}
        </span>
        <Button icon="cross" intent="danger" variant="outlined" size="small" onClick={handleCancel} disabled={!!pendingAction}>Cancel</Button>
      </div>
    );
  }
  if (s === "sample_review") {
    return (
      <>
        {errorEl}
        <Button intent="warning" icon="eye-open" onClick={() => onSwitchTab?.("strategy")}>
          Review Sample Below
        </Button>
      </>
    );
  }
  if (s === "completed") {
    return (
      <>
        {errorEl}
        <Button icon="refresh" variant="outlined" loading={isBusy} disabled={isBusy} onClick={handleGenerateStrategy}>Re-run</Button>
      </>
    );
  }
  if (s.startsWith("failed_")) {
    return (
      <>
        {errorEl}
        <Button icon="refresh" intent="primary" loading={isBusy} disabled={isBusy} onClick={handleGenerateStrategy}>Retry</Button>
      </>
    );
  }
  return errorEl;
}

// ── Strategy Tab ──

const SENIORITY_OPTIONS = [
  { value: "founder", label: "Founder" },
  { value: "c_suite", label: "C-Suite" },
  { value: "vp", label: "VP" },
  { value: "partner", label: "Partner" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
  { value: "senior", label: "Senior" },
  { value: "entry", label: "Entry" },
];

const SIZE_OPTIONS = [
  "1-10", "11-50", "51-200",
  "201-500", "501-1000", "1001-5000", "5001-10000", "10001+",
];

const DEPARTMENT_OPTIONS = [
  "Business Development", "Consulting", "Customer Service",
  "Engineering & Technical", "Finance", "General Management",
  "Health Care & Medical", "Human Resources", "Information Technology",
  "Legal", "Marketing", "Operations", "Product",
  "Research & Analytics", "Sales",
];

function StrategyTab({ bp, onUpdate }: { bp: Blueprint; onUpdate: () => void }) {
  const [strategyData, setStrategyData] = useState<StrategyResponse | null>(null);
  const [pipelineRun, setPipelineRun] = useState<PipelineRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editIcp, setEditIcp] = useState<ICP | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchStrategy = useCallback(() => {
    Promise.all([
      api.get<StrategyResponse>(`/blueprints/${bp.id}/strategy`).catch(() => null),
      api.get<PipelineRun | null>(`/blueprints/${bp.id}/pipeline-status`).catch(() => null),
    ]).then(([strat, run]) => {
      setStrategyData(strat);
      setPipelineRun(run);
    }).finally(() => setLoading(false));
  }, [bp.id, bp.status]);

  useEffect(() => { fetchStrategy(); }, [fetchStrategy]);

  useEffect(() => {
    const isRunning = ["planning", "gathering", "enriching", "scoring", "sample_enriching"].includes(bp.status);
    if (!isRunning) return;
    const interval = setInterval(fetchStrategy, 3000);
    return () => clearInterval(interval);
  }, [bp.status, fetchStrategy]);

  const handleSaveEdits = async () => {
    if (!editIcp) return;
    setSaving(true);
    try {
      const result = await api.put<StrategyResponse>(`/blueprints/${bp.id}/strategy`, { icp: editIcp });
      setStrategyData(result);
      setEditing(false);
      onUpdate();
    } catch {}
    finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={28} /></div>;
  }

  if (bp.status === "draft") {
    return (
      <div style={{ paddingTop: 20 }}>
        <NonIdealState
          icon="search-template"
          title="No strategy generated yet"
          description={
            bp.requirement_text
              ? 'Click "Generate Strategy" in the header to analyze your requirements.'
              : "Add targeting requirements in the Info tab first, then generate a strategy."
          }
        />
      </div>
    );
  }

  const isRunning = ["planning", "gathering", "enriching", "scoring"].includes(bp.status);

  if (isRunning) {
    if (bp.status === "planning") {
      return (
        <div style={{ paddingTop: 20 }}>
          <Card elevation={Elevation.ONE} style={{ padding: 40, textAlign: "center" }}>
            <Spinner size={32} />
            <h4 style={{ margin: "16px 0 8px", fontSize: 16, fontWeight: 600, color: "#182026" }}>
              Generating Strategy
            </h4>
            <p style={{ margin: 0, fontSize: 13, color: "#5c7080", maxWidth: 400, marginInline: "auto", lineHeight: 1.6 }}>
              Analyzing your requirements and building a targeting strategy with ICP,
              gather plan, and enrichment priorities. This typically takes 30-60 seconds.
            </p>
            <ProgressBar intent="primary" animate stripes style={{ marginTop: 20, maxWidth: 320, marginInline: "auto" }} />
          </Card>
        </div>
      );
    }
    if (pipelineRun) {
      return <PipelineProgress run={pipelineRun} />;
    }
    return (
      <div style={{ paddingTop: 20 }}>
        <Card elevation={Elevation.ONE} style={{ padding: 40, textAlign: "center" }}>
          <Spinner size={32} />
          <h4 style={{ margin: "16px 0 8px", fontSize: 16, fontWeight: 600 }}>
            {RUNNING_LABELS[bp.status] || "Pipeline Running"}
          </h4>
          <ProgressBar intent="primary" animate stripes style={{ marginTop: 16, maxWidth: 320, marginInline: "auto" }} />
        </Card>
      </div>
    );
  }

  if (bp.status === "sample_enriching") {
    return <SampleEnrichingView bp={bp} onUpdate={onUpdate} />;
  }

  if (bp.status === "sample_review") {
    return <SampleReviewPanel bp={bp} onUpdate={onUpdate} />;
  }

  const strategy = strategyData?.targeting_strategy as Record<string, unknown> | null;
  const icp = (strategy?.icp || {}) as unknown as ICP;
  const sourcePlan = strategyData?.source_plan as SourcePlan | null;

  if (!strategy || !icp.titles) {
    return (
      <div style={{ paddingTop: 20 }}>
        <NonIdealState icon="search-template" title="Strategy not available" description="Generate a strategy to see targeting criteria." />
      </div>
    );
  }

  if (bp.status.startsWith("failed_")) {
    return (
      <div style={{ paddingTop: 20 }}>
        <Callout intent="danger" icon="error" title="Pipeline Failed" style={{ marginBottom: 16 }}>
          {pipelineRun?.error_message || "An error occurred during pipeline execution."}
        </Callout>
        <ICPDisplay icp={icp} />
      </div>
    );
  }

  if (bp.status === "completed" && pipelineRun?.result_summary) {
    const summary = pipelineRun.result_summary as Record<string, number>;
    // `leads_created` is per-run delta — when the user clicks Approve & Gather All
    // after a sample review, the leads are already in the DB and the delta is 0,
    // which reads as a failure to a casual user. Prefer the absolute pipeline
    // total (bp.lead_count) and only show the per-run delta as an aside.
    const newThisRun = summary.leads_created ?? 0;
    const totalLeads = summary.total_leads ?? bp.lead_count ?? newThisRun;
    return (
      <div style={{ paddingTop: 20 }}>
        <Callout intent="success" icon="tick-circle" title="Pipeline Complete" style={{ marginBottom: 20 }}>
          {summary.companies_gathered ?? 0} companies gathered.{" "}
          {totalLeads} {totalLeads === 1 ? "lead" : "leads"} in pipeline
          {newThisRun > 0 ? ` (${newThisRun} new this run)` : ""}.{" "}
          {summary.enrichment_actions ? `${summary.enrichment_actions} enrichment actions. ` : ""}
          {summary.avg_score ? `Average score: ${summary.avg_score}` : ""}
        </Callout>
        <ICPDisplay icp={icp} />
        {sourcePlan && <GatherPlanDisplay plan={sourcePlan as unknown as Record<string, unknown>} />}
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 20 }}>
      {editing ? (
        <ICPEditor
          icp={editIcp!}
          onChange={setEditIcp}
          onSave={handleSaveEdits}
          onCancel={() => setEditing(false)}
          saving={saving}
        />
      ) : (
        <>
          {bp.status === "strategy_review" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <Button icon="edit" onClick={() => { setEditIcp({ ...icp, business_types: icp.business_types || [], exclusions: { ...icp.exclusions } }); setEditing(true); }}>
                Edit ICP
              </Button>
            </div>
          )}
          <ICPDisplay icp={icp} />
        </>
      )}

      {sourcePlan && <GatherPlanDisplay plan={sourcePlan as unknown as Record<string, unknown>} />}

      {Boolean(strategy.rationale) && (
        <Card elevation={Elevation.ONE} style={{ padding: 20, marginTop: 16 }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>AI Rationale</h4>
          <p style={{ margin: 0, fontSize: 13, color: "#5c7080", lineHeight: 1.6 }}>{String(strategy.rationale)}</p>
        </Card>
      )}

      {Boolean(strategy.messaging_angles) && (strategy.messaging_angles as string[]).length > 0 && (
        <Card elevation={Elevation.ONE} style={{ padding: 20, marginTop: 16 }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>Messaging Angles</h4>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {(strategy.messaging_angles as string[]).map((a, i) => (
              <li key={i} style={{ fontSize: 13, color: "#5c7080", marginBottom: 4 }}>{a}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

// ── Sample Enriching (live view) ──

function SampleEnrichingView({ bp, onUpdate }: { bp: Blueprint; onUpdate: () => void }) {
  const [sampleData, setSampleData] = useState<SampleReviewData | null>(null);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const data = await api.get<SampleReviewData>(`/blueprints/${bp.id}/sample-review`);
        if (active) setSampleData(data);
      } catch { /* ignore */ }
    };
    poll();
    const interval = setInterval(poll, 4000);
    return () => { active = false; clearInterval(interval); };
  }, [bp.id]);

  const companies = sampleData?.companies || [];
  const enrichedCount = companies.filter(c => c.description || c.employee_count || c.linkedin_url).length;
  const total = companies.length;
  const progress = total > 0 ? enrichedCount / total : 0;

  return (
    <div style={{ paddingTop: 20 }}>
      <Callout intent="primary" icon="search-template" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <strong>Enriching Sample — {enrichedCount} / {total} companies</strong>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#5c7080" }}>
              AI is scraping websites, finding contacts, and enriching each company. Results appear below as they complete.
            </p>
          </div>
          <Spinner size={20} />
        </div>
        <ProgressBar intent="primary" animate stripes value={progress} style={{ marginTop: 12 }} />
      </Callout>

      {companies.map(c => {
        const done = !!(c.description || c.employee_count || c.linkedin_url);
        return (
          <Card key={c.id} elevation={Elevation.ONE} style={{ padding: "12px 16px", marginBottom: 6, opacity: done ? 1 : 0.5, transition: "opacity .3s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {done ? <Icon icon="tick-circle" intent="success" size={16} /> : <Spinner size={14} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "#5c7080" }}>
                  {[c.domain, c.industry || c.category, c.location].filter(Boolean).join(" · ")}
                </div>
              </div>
              {done && c.contacts?.length > 0 && (
                <Tag minimal round>{c.contacts.length} contacts</Tag>
              )}
              {done && c.description && (
                <span style={{ fontSize: 12, color: "#5c7080", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                  {c.description}
                </span>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── Sample Review ──

function CompanyCard({ company, isEnriched, defaultOpen }: { company: SampleCompany; isEnriched: boolean; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const c = company;
  const contactCount = c.contacts?.length || 0;

  return (
    <Card elevation={Elevation.ONE} style={{ padding: 0, marginBottom: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
          background: open ? "#f0f4f8" : "transparent", transition: "background .15s",
        }}
      >
        <Icon icon={open ? "chevron-down" : "chevron-right"} size={14} color="#5c7080" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</span>
            {c.domain && (
              <a href={`https://${c.domain}`} target="_blank" rel="noreferrer"
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 12, color: "#2b95d6", textDecoration: "none" }}>
                {c.domain}
              </a>
            )}
            {isEnriched && c.description && (
              <Tag minimal round intent="success" style={{ fontSize: 10 }}>enriched</Tag>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#5c7080", marginTop: 2 }}>
            {[c.industry || c.category, c.location, c.employee_count ? `${c.employee_count} employees` : null]
              .filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <Tag minimal round style={{ flexShrink: 0 }}>
          {contactCount} {contactCount === 1 ? "contact" : "contacts"}
        </Tag>
      </div>

      <Collapse isOpen={open}>
        <div style={{ padding: "0 16px 16px", borderTop: "1px solid #e1e8ed" }}>
          {isEnriched && c.description && (
            <p style={{ fontSize: 13, color: "#5c7080", margin: "12px 0", lineHeight: 1.5 }}>{c.description}</p>
          )}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, marginBottom: contactCount > 0 ? 12 : 0 }}>
            {c.domain && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: "#5c7080" }}>Website: </span>
                <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" style={{ color: "#2b95d6" }}>{c.domain}</a>
              </div>
            )}
            {c.phone && <div style={{ fontSize: 12 }}><span style={{ color: "#5c7080" }}>Phone: </span>{c.phone}</div>}
            {c.linkedin_url && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: "#5c7080" }}>LinkedIn: </span>
                <a href={c.linkedin_url} target="_blank" rel="noreferrer" style={{ color: "#2b95d6" }}>View Profile</a>
              </div>
            )}
            {c.employee_count && <div style={{ fontSize: 12 }}><span style={{ color: "#5c7080" }}>Employees: </span>{c.employee_count.toLocaleString()}</div>}
          </div>
          {contactCount > 0 && (
            <table className="lead-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>LinkedIn</th>
                </tr>
              </thead>
              <tbody>
                {c.contacts.map(ct => (
                  <tr key={ct.id}>
                    <td style={{ fontWeight: 500 }}>{ct.first_name} {ct.last_name}</td>
                    <td>{ct.job_title || "—"}</td>
                    <td style={{ color: ct.email ? "#1c2127" : "#5c7080" }}>
                      {ct.email || (ct.has_email_available
                        ? <Tag minimal intent="success" style={{ fontSize: 10 }}>available</Tag>
                        : "—")}
                    </td>
                    <td style={{ color: ct.phone ? "#1c2127" : "#5c7080" }}>
                      {ct.phone || (ct.has_phone_available
                        ? <Tag minimal intent="success" style={{ fontSize: 10 }}>available</Tag>
                        : "—")}
                    </td>
                    <td>
                      {ct.linkedin_url ? (
                        <a href={ct.linkedin_url} target="_blank" rel="noreferrer" style={{ color: "#2b95d6", fontSize: 12 }}>View</a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {contactCount === 0 && (
            <div style={{ fontSize: 12, color: "#8a9ba8", fontStyle: "italic", padding: "8px 0" }}>
              No contacts found at this company yet.
            </div>
          )}
        </div>
      </Collapse>
    </Card>
  );
}

function SampleReviewPanel({ bp, onUpdate }: { bp: Blueprint; onUpdate: () => void }) {
  const [sampleData, setSampleData] = useState<SampleReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const actionInFlight = useRef(false);
  const [showRestrategise, setShowRestrategise] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  const fetchSample = useCallback(() => {
    api.get<SampleReviewData>(`/blueprints/${bp.id}/sample-review`)
      .then(setSampleData)
      .catch((e) => setError(e?.message || "Failed to load sample data"))
      .finally(() => setLoading(false));
  }, [bp.id]);

  useEffect(() => { fetchSample(); }, [fetchSample]);

  const runSampleAction = async (action: () => Promise<void>) => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setActionLoading(true); setError("");
    try {
      await action();
      onUpdate();
    } catch (e: any) { setError(e?.message || "Failed"); }
    finally { setActionLoading(false); actionInFlight.current = false; }
  };

  const handleEnrich = () => runSampleAction(() => api.post(`/blueprints/${bp.id}/enrich-sample`));
  const handleApprove = (skipEnrichment = false) => runSampleAction(() =>
    api.post(`/blueprints/${bp.id}/approve-sample`, { skip_enrichment: skipEnrichment })
  );

  const handleRestrategise = async () => {
    if (!feedback.trim() || actionInFlight.current) return;
    actionInFlight.current = true;
    setActionLoading(true); setError("");
    try {
      await api.post(`/blueprints/${bp.id}/restrategise`, { feedback: feedback.trim() });
      setShowRestrategise(false);
      setFeedback("");
      onUpdate();
    } catch (e: any) { setError(e?.message || "Failed"); }
    finally { setActionLoading(false); actionInFlight.current = false; }
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={28} /></div>;
  }

  const companies = sampleData?.companies || [];
  const totalContacts = companies.reduce((sum, c) => sum + (c.contacts?.length || 0), 0);
  const hasCompanies = companies.length > 0;
  const isEnriched = sampleData?.is_enriched ?? false;
  const gatherWarnings = sampleData?.gather_warnings || [];

  return (
    <div style={{ paddingTop: 20 }}>
      {error && <Callout intent="danger" style={{ marginBottom: 16 }}>{error}</Callout>}
      {gatherWarnings.map((w, i) => (
        <Callout key={i} intent="warning" icon="warning-sign" style={{ marginBottom: 12, fontSize: 13 }}>
          {w}
        </Callout>
      ))}

      <Callout intent="primary" icon="eye-open" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <strong>Sample Review{isEnriched ? " — Enriched" : ""}</strong>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#5c7080" }}>
              {isEnriched
                ? `${companies.length} companies and ${totalContacts} contacts enriched. Review the targeting below — click a company to see its contacts and details.`
                : `${companies.length} companies gathered with ${totalContacts} contacts. Enrich the sample to see detailed data before deciding.`
              }
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, marginLeft: 16 }}>
            <Button intent="danger" variant="outlined" icon="refresh"
              onClick={() => setShowRestrategise(true)} loading={actionLoading}>
              Restrategise
            </Button>
            {!isEnriched && hasCompanies && (
              <Button intent="primary" icon="search-template" onClick={handleEnrich} loading={actionLoading}>
                Enrich Samples
              </Button>
            )}
            {hasCompanies && (
              <Button intent="success" icon="tick" onClick={() => handleApprove(true)} loading={actionLoading}>
                Approve & Gather All
              </Button>
            )}
            {isEnriched && (
              <Button intent="success" icon="tick" onClick={() => handleApprove(false)} loading={actionLoading}>
                Approve & Run Full Pipeline
              </Button>
            )}
          </div>
        </div>
      </Callout>

      {hasCompanies ? (
        <div>
          {companies.map((c, i) => (
            <CompanyCard key={c.id} company={c} isEnriched={isEnriched} defaultOpen={i === 0} />
          ))}
        </div>
      ) : (
        <Callout intent="warning" icon="warning-sign" style={{ marginBottom: 16 }}>
          No sample results were gathered. The search criteria may be too narrow. Try restrategising with feedback.
        </Callout>
      )}

      <Dialog isOpen={showRestrategise} onClose={() => setShowRestrategise(false)} title="Restrategise" style={{ width: 560 }}>
        <DialogBody>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#5c7080" }}>
            Explain what's wrong with the current results so the AI can generate a better strategy.
            Be specific about what types of companies you expected vs what was returned.
          </p>
          <FormGroup label="Why didn't these results work?" labelInfo="(required)">
            <TextArea fill rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g. These are all insurance agencies. I need data analytics companies and SaaS platforms that sell licensed datasets..."
              autoFocus />
          </FormGroup>
        </DialogBody>
        <DialogFooter actions={
          <>
            <Button onClick={() => setShowRestrategise(false)}>Cancel</Button>
            <Button intent="primary" loading={actionLoading} disabled={!feedback.trim()} onClick={handleRestrategise}>
              Restrategise with Feedback
            </Button>
          </>
        } />
      </Dialog>
    </div>
  );
}

// ── ICP Display ──

function ICPDisplay({ icp }: { icp: ICP }) {
  return (
    <Card elevation={Elevation.ONE} style={{ padding: 20, marginBottom: 16 }}>
      <h4 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>Ideal Customer Profile</h4>
      <div className="icp-grid">
        <ICPSection label="Job Titles" items={icp.titles} />
        <ICPSection label="Seniority" items={icp.seniority_levels?.map((s) => {
          const m: Record<string, string> = { founder: "Founder", c_suite: "C-Suite", vp: "VP", partner: "Partner", director: "Director", manager: "Manager", senior: "Senior", entry: "Entry" };
          return m[s] || s;
        })} />
        {icp.departments?.length > 0 && <ICPSection label="Departments" items={icp.departments} />}
        <ICPSection label="Industries" items={icp.industries} />
        <ICPSection label="Company Size" items={icp.company_size_ranges?.map((r) => `${r} employees`)} />
        <ICPSection label="Geographies" items={icp.geographies} />
        <ICPSection label="Keywords" items={icp.keywords} />
        <ICPSection label="Business Types" items={icp.business_types} />
      </div>
      {icp.exclusions && (icp.exclusions.titles?.length > 0 || icp.exclusions.industries?.length > 0) && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #edeff2" }}>
          <span style={{ fontSize: 12, color: "#8a9ba8", fontWeight: 500 }}>Exclusions: </span>
          {icp.exclusions.titles?.map((t, i) => <Tag key={`t${i}`} minimal round style={{ marginRight: 4 }}>{t}</Tag>)}
          {icp.exclusions.industries?.map((ind, i) => <Tag key={`i${i}`} minimal round intent="warning" style={{ marginRight: 4 }}>{ind}</Tag>)}
        </div>
      )}
    </Card>
  );
}

function ICPSection({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="icp-section">
      <div className="icp-section-label">{label}</div>
      <div className="icp-section-tags">
        {items.map((item, i) => <Tag key={i} minimal round>{item}</Tag>)}
      </div>
    </div>
  );
}

// ── ICP Editor ──

function ICPEditor({
  icp, onChange, onSave, onCancel, saving,
}: {
  icp: ICP; onChange: (icp: ICP) => void; onSave: () => void; onCancel: () => void; saving: boolean;
}) {
  const updateField = (field: keyof ICP, value: unknown) => onChange({ ...icp, [field]: value });

  const toggleSeniority = (val: string) => {
    const current = icp.seniority_levels || [];
    updateField("seniority_levels", current.includes(val) ? current.filter((s) => s !== val) : [...current, val]);
  };

  const toggleSize = (val: string) => {
    const current = icp.company_size_ranges || [];
    updateField("company_size_ranges", current.includes(val) ? current.filter((s) => s !== val) : [...current, val]);
  };

  return (
    <Card elevation={Elevation.ONE} style={{ padding: 24, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Edit Ideal Customer Profile</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="small" onClick={onCancel}>Cancel</Button>
          <Button size="small" intent="primary" loading={saving} onClick={onSave}>Save Changes</Button>
        </div>
      </div>

      <TagListEditor label="Job Titles" tags={icp.titles || []} onChange={(v) => updateField("titles", v)} placeholder="Add a job title..." />

      <FormGroup label="Seniority Levels" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SENIORITY_OPTIONS.map((opt) => (
            <Tag
              key={opt.value}
              interactive
              round
              minimal={!icp.seniority_levels?.includes(opt.value)}
              intent={icp.seniority_levels?.includes(opt.value) ? "primary" : "none"}
              onClick={() => toggleSeniority(opt.value)}
              style={{ cursor: "pointer" }}
            >
              {opt.label}
            </Tag>
          ))}
        </div>
      </FormGroup>

      <TagListEditor label="Industries" tags={icp.industries || []} onChange={(v) => updateField("industries", v)} placeholder="Add an industry..." />

      <FormGroup label="Departments" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {DEPARTMENT_OPTIONS.map((dept) => (
            <Tag
              key={dept}
              interactive
              round
              minimal={!icp.departments?.includes(dept)}
              intent={icp.departments?.includes(dept) ? "primary" : "none"}
              onClick={() => {
                const current = icp.departments || [];
                updateField("departments", current.includes(dept) ? current.filter((d: string) => d !== dept) : [...current, dept]);
              }}
              style={{ cursor: "pointer" }}
            >
              {dept}
            </Tag>
          ))}
        </div>
      </FormGroup>

      <FormGroup label="Company Size Ranges" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SIZE_OPTIONS.map((opt) => (
            <Tag
              key={opt}
              interactive
              round
              minimal={!icp.company_size_ranges?.includes(opt)}
              intent={icp.company_size_ranges?.includes(opt) ? "primary" : "none"}
              onClick={() => toggleSize(opt)}
              style={{ cursor: "pointer" }}
            >
              {opt}
            </Tag>
          ))}
        </div>
      </FormGroup>

      <TagListEditor label="Geographies" tags={icp.geographies || []} onChange={(v) => updateField("geographies", v)} placeholder="Add a geography..." />
      <TagListEditor label="Keywords" tags={icp.keywords || []} onChange={(v) => updateField("keywords", v)} placeholder="Add a keyword..." />
      <TagListEditor label="Business Types" tags={icp.business_types || []} onChange={(v) => updateField("business_types", v)} placeholder="e.g. restaurant, clinic, salon..." />
    </Card>
  );
}

function TagListEditor({ label, tags, onChange, placeholder }: {
  label: string; tags: string[]; onChange: (tags: string[]) => void; placeholder: string;
}) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) {
      onChange([...tags, val]);
    }
    setInput("");
  };

  return (
    <FormGroup label={label} style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
        {tags.map((tag, i) => (
          <Tag key={i} minimal round onRemove={() => onChange(tags.filter((_, j) => j !== i))}>{tag}</Tag>
        ))}
      </div>
      <InputGroup
        small
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
        rightElement={<Button minimal icon="add" size="small" onClick={addTag} />}
        style={{ maxWidth: 300 }}
      />
    </FormGroup>
  );
}

// ── Source Plan Display ──

function SourcePlanDisplay({ plan }: { plan: SourcePlan }) {
  return (
    <Card elevation={Elevation.ONE} style={{ padding: 20, marginBottom: 16 }}>
      <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600 }}>Source Plan</h4>
      {plan.sources.length === 0 ? (
        <Callout intent="warning" icon="warning-sign">
          No active search sources are linked. Connect a data source in the Settings tab.
        </Callout>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {plan.sources.map((src) => (
            <div key={src.source_id} className="source-plan-card">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <Icon icon="data-connection" size={16} />
                <span style={{ fontWeight: 500, fontSize: 14 }}>{src.source_name}</span>
                <Tag minimal round>{({ lusha: "B2B Database", lusha_search: "B2B Database", apollo: "B2B Database", apollo_search: "B2B Database", google_places: "Google Places", osm: "OpenStreetMap", b2b_database: "B2B Database" } as Record<string, string>)[src.source_type] || src.source_type}</Tag>
                <span style={{ marginLeft: "auto", fontSize: 13, color: "#5c7080" }}>
                  ~{src.estimated_results} leads
                </span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {src.matched_criteria.map((c) => (
                  <Tag key={c} minimal round intent="success" icon="tick">{c}</Tag>
                ))}
                {src.unmatched_criteria.map((c) => (
                  <Tag key={c} minimal round intent="warning" icon="disable">{c}</Tag>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {plan.warnings.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {plan.warnings.map((w, i) => (
            <Callout key={i} intent="warning" icon="info-sign" style={{ marginTop: 8, fontSize: 13 }}>{w}</Callout>
          ))}
        </div>
      )}
      {plan.recommendations.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {plan.recommendations.map((r, i) => (
            <Callout key={i} icon="lightbulb" style={{ marginTop: 8, fontSize: 13 }}>{r}</Callout>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Pipeline Progress ──

const PIPELINE_STAGES = [
  { key: "planning", label: "Strategy" },
  { key: "gathering", label: "Gathering" },
  { key: "enriching", label: "AI Enrichment" },
  { key: "scoring", label: "Scoring" },
];

function PipelineProgress({ run }: { run: PipelineRun }) {
  const progress = run.progress || {};

  const gatherProgress = progress.gathering as Record<string, unknown> | undefined;
  const enrichProgress = progress.enriching as Record<string, unknown> | undefined;
  const scoreProgress = progress.scoring as Record<string, unknown> | undefined;

  const stageStatuses = PIPELINE_STAGES.map(s => {
    const p = progress[s.key] as Record<string, unknown> | undefined;
    return (p?.status as string) || "pending";
  });

  return (
    <div style={{ paddingTop: 20 }}>
      <Card elevation={Elevation.ONE} style={{ padding: 24 }}>
        <h4 style={{ margin: "0 0 24px", fontSize: 15, fontWeight: 600 }}>Pipeline Running</h4>

        <div className="pipeline-tracker">
          {PIPELINE_STAGES.map((stage, i) => {
            const status = stageStatuses[i];
            let cls = "pt-step";
            if (status === "completed") cls += " completed";
            else if (status === "in_progress") cls += " active";
            else if (status === "failed") cls += " failed";

            const showConnector = i < PIPELINE_STAGES.length - 1;
            const nextDone = i < PIPELINE_STAGES.length - 1 && stageStatuses[i] === "completed";

            return (
              <div key={stage.key} className="pt-item">
                <div className={cls}>
                  <div className="pt-circle">
                    {status === "completed" ? <Icon icon="tick" size={14} /> :
                     status === "in_progress" ? <Spinner size={14} /> :
                     status === "failed" ? <Icon icon="cross" size={14} /> :
                     <span>{i + 1}</span>}
                  </div>
                  <span className="pt-label">{stage.label}</span>
                </div>
                {showConnector && (
                  <div className={`pt-connector${nextDone ? " done" : ""}`} />
                )}
              </div>
            );
          })}
        </div>

        {gatherProgress && gatherProgress.status === "in_progress" && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: "#5c7080", marginBottom: 8 }}>
              <Icon icon="search" size={12} style={{ marginRight: 4 }} />
              Gathering from {String(gatherProgress.source || "data sources")}
              {gatherProgress.companies_found ? ` · ${String(gatherProgress.companies_found)} companies found` : ""}
            </div>
            <ProgressBar intent="primary" animate stripes />
          </div>
        )}

        {enrichProgress && enrichProgress.status === "in_progress" && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: "#5c7080", marginBottom: 8 }}>
              <Icon icon="predictive-analysis" size={12} style={{ marginRight: 4 }} />
              AI Enrichment Pass {String(enrichProgress.current_pass || 1)} ·{" "}
              {String(enrichProgress.companies_enriched || 0)} / {String(enrichProgress.total_companies || 0)} companies
              {enrichProgress.leads_created ? ` · ${String(enrichProgress.leads_created)} leads created` : ""}
              {enrichProgress.enrichment_actions ? ` · ${String(enrichProgress.enrichment_actions)} actions` : ""}
            </div>
            <ProgressBar
              value={
                (enrichProgress.total_companies as number) > 0
                  ? (enrichProgress.companies_enriched as number) / (enrichProgress.total_companies as number)
                  : undefined
              }
              intent="primary"
              animate
              stripes
            />
          </div>
        )}

        {scoreProgress && scoreProgress.status === "in_progress" && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: "#5c7080", marginBottom: 8 }}>
              Scoring: {String(scoreProgress.completed || 0)} / {String(scoreProgress.total || 0)}
            </div>
            <ProgressBar
              value={(scoreProgress.total as number) > 0 ? (scoreProgress.completed as number) / (scoreProgress.total as number) : undefined}
              intent="primary"
              animate
              stripes
            />
          </div>
        )}
      </Card>
    </div>
  );
}

function GatherPlanDisplay({ plan }: { plan: Record<string, unknown> }) {
  const primarySource = String(plan.primary_source || "unknown");
  const queries = (plan.search_queries || []) as string[];
  const geoTargets = (plan.geographic_targets || []) as string[];
  const bizTypes = (plan.business_type_filters || []) as string[];
  const estimated = Number(plan.estimated_companies || 0);
  const rationale = String(plan.rationale || "");

  const sourceLabels: Record<string, string> = {
    google_places: "Google Places API",
    osm: "OpenStreetMap (Free)",
    apollo_search: "B2B Contact Search",
    lusha: "B2B Contact Search",
    lusha_search: "B2B Contact Search",
  };

  return (
    <Card elevation={Elevation.ONE} style={{ padding: 20, marginBottom: 16 }}>
      <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600 }}>Gather Plan</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Icon icon="data-connection" size={16} />
        <span style={{ fontWeight: 500, fontSize: 14 }}>
          {sourceLabels[primarySource] || primarySource}
        </span>
        <Tag minimal round intent="primary">{sourceLabels[primarySource] || primarySource}</Tag>
        {estimated > 0 && (
          <span style={{ marginLeft: "auto", fontSize: 13, color: "#5c7080" }}>
            ~{estimated} companies expected
          </span>
        )}
      </div>

      {queries.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#8a9ba8", fontWeight: 500 }}>Search Queries: </span>
          {queries.map((q, i) => <Tag key={i} minimal round style={{ marginRight: 4, marginBottom: 4 }}>{q}</Tag>)}
        </div>
      )}

      {geoTargets.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#8a9ba8", fontWeight: 500 }}>Geographic Targets: </span>
          {geoTargets.map((g, i) => <Tag key={i} minimal round intent="success" style={{ marginRight: 4 }}>{g}</Tag>)}
        </div>
      )}

      {bizTypes.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: "#8a9ba8", fontWeight: 500 }}>Business Types: </span>
          {bizTypes.map((b, i) => <Tag key={i} minimal round style={{ marginRight: 4 }}>{b}</Tag>)}
        </div>
      )}

      {rationale && (
        <p style={{ fontSize: 13, color: "#5c7080", marginTop: 8, marginBottom: 0, lineHeight: 1.6 }}>{rationale}</p>
      )}
    </Card>
  );
}

// ── Tab 1: Overview ──

const STATUS_COLORS: Record<string, string> = {
  unclaimed: "#8a9ba8",
  claimed: "#d9822b",
  contacted: "#2b95d6",
  qualified: "#0f9960",
  won: "#634dbf",
  dead: "#d13913",
};

const STATUS_LABELS: Record<string, string> = {
  unclaimed: "Unclaimed",
  claimed: "Claimed",
  contacted: "Contacted",
  qualified: "Qualified",
  won: "Won",
  dead: "Dead",
};

function OverviewTab({ blueprintId }: { blueprintId: string }) {
  const [data, setData] = useState<BlueprintOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");

  useEffect(() => {
    setLoading(true);
    setOverviewError("");
    api.get<BlueprintOverview>(`/blueprints/${blueprintId}/overview`)
      .then(setData)
      .catch((e) => setOverviewError(e?.message || "Failed to load overview"))
      .finally(() => setLoading(false));
  }, [blueprintId]);

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><Spinner size={28} /></div>;
  }

  if (!data) {
    return <NonIdealState icon="error" title="Failed to load overview" description={overviewError || "Please try refreshing."} />;
  }

  const sb = data.status_breakdown;

  return (
    <div className="overview-tab">
      {/* KPI Cards */}
      <div className="overview-metrics">
        <OverviewMetric label="Total Leads" value={data.total_leads} icon="people" />
        <OverviewMetric label="Claimed" value={data.leads_claimed} icon="hand" />
        <OverviewMetric label="Qualified" value={data.leads_qualified} icon="tick-circle" />
        <OverviewMetric label="Conversion" value={`${data.conversion_rate}%`} icon="percentage" />
        <OverviewMetric label="Avg. Score" value={data.avg_lead_score} icon="trending-up" />
      </div>

      <div className="overview-panels">
        {/* Lead Pipeline */}
        <div className="overview-panel">
          <div className="overview-panel-header">
            <h4>Lead Pipeline</h4>
          </div>
          <div className="overview-panel-body">
            {data.total_leads === 0 ? (
              <div style={{ padding: 32 }}><NonIdealState icon="horizontal-bar-chart" title="No leads yet" description="Leads will appear here once generated." /></div>
            ) : (
              <>
                <div className="pipeline-bar">
                  {(Object.keys(STATUS_COLORS) as Array<keyof LeadStatusBreakdown>).map((s) => {
                    const count = sb[s];
                    if (count === 0) return null;
                    const pct = (count / data.total_leads) * 100;
                    return (
                      <div
                        key={s}
                        className="pipeline-segment"
                        style={{ width: `${pct}%`, background: STATUS_COLORS[s] }}
                        title={`${STATUS_LABELS[s]}: ${count} (${pct.toFixed(1)}%)`}
                      />
                    );
                  })}
                </div>
                <div className="pipeline-legend">
                  {(Object.keys(STATUS_COLORS) as Array<keyof LeadStatusBreakdown>).map((s) => (
                    <div key={s} className="pipeline-legend-item">
                      <span className="pipeline-legend-dot" style={{ background: STATUS_COLORS[s] }} />
                      <span className="pipeline-legend-label">{STATUS_LABELS[s]}</span>
                      <span className="pipeline-legend-count">{sb[s]}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Follow-ups */}
        <div className="overview-panel">
          <div className="overview-panel-header">
            <h4>Follow-ups</h4>
          </div>
          <div className="overview-panel-body">
            <div className="followup-stats">
              <div className="followup-stat">
                <div className="followup-stat-value" style={{ color: "#2b95d6" }}>{data.follow_ups.upcoming}</div>
                <div className="followup-stat-label">Upcoming</div>
              </div>
              <div className="followup-stat">
                <div className="followup-stat-value" style={{ color: "#d13913" }}>{data.follow_ups.overdue}</div>
                <div className="followup-stat-label">Overdue</div>
              </div>
              <div className="followup-stat">
                <div className="followup-stat-value" style={{ color: "#0f9960" }}>{data.follow_ups.completed}</div>
                <div className="followup-stat-label">Completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overview-panels">
        {/* Team Performance */}
        <div className="overview-panel">
          <div className="overview-panel-header">
            <h4>Team Performance</h4>
          </div>
          <div className="overview-panel-body overview-panel-body--flush">
            {data.member_performance.length === 0 ? (
              <div style={{ padding: 32 }}><NonIdealState icon="people" title="No claimed leads" description="Performance stats appear when team members claim leads." /></div>
            ) : (
              <table className="lead-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th style={{ textAlign: "right" }}>Claimed</th>
                    <th style={{ textAlign: "right" }}>Contacted</th>
                    <th style={{ textAlign: "right" }}>Qualified</th>
                    <th style={{ textAlign: "right" }}>Won</th>
                    <th style={{ textAlign: "right" }}>Dead</th>
                  </tr>
                </thead>
                <tbody>
                  {data.member_performance.map((m) => (
                    <tr key={m.user_id}>
                      <td style={{ fontWeight: 500 }}>{m.user_name}</td>
                      <td style={{ textAlign: "right" }}>{m.leads_claimed}</td>
                      <td style={{ textAlign: "right" }}>{m.leads_contacted}</td>
                      <td style={{ textAlign: "right" }}>
                        <Tag minimal round intent="success">{m.leads_qualified}</Tag>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Tag minimal round intent="primary">{m.leads_won}</Tag>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {m.leads_dead > 0 ? <Tag minimal round intent="danger">{m.leads_dead}</Tag> : <span style={{ color: "#8a9ba8" }}>0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="overview-panel">
          <div className="overview-panel-header">
            <h4>Recent Activity</h4>
          </div>
          <div className="overview-panel-body">
            {data.recent_activities.length === 0 ? (
              <div style={{ padding: 32 }}><NonIdealState icon="timeline-events" title="No activity" description="Activity will appear as the blueprint progresses." /></div>
            ) : (
              <ul className="activity-list">
                {data.recent_activities.map((a) => {
                  const info = ACTION_LABELS[a.action] || { text: a.action.replace(/_/g, " "), dot: a.entity_type === "lead" ? "orange" : "blue" };
                  return (
                    <li key={a.id} className="activity-item">
                      <div className={`activity-dot ${info.dot}`} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="activity-text">
                          <strong>{a.user_name}</strong> {info.text}{" "}
                          {a.entity_name ? <strong>{a.entity_name}</strong> : a.entity_type}
                        </div>
                        <div className="activity-time">
                          {new Date(a.created_at).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverviewMetric({ label, value, icon }: { label: string; value: number | string; icon: string }) {
  return (
    <div className="overview-metric-card">
      <div className="overview-metric-header">
        <span className="overview-metric-label">{label}</span>
        <Icon icon={icon as any} size={15} color="#5c7080" />
      </div>
      <div className="overview-metric-value">{typeof value === "number" ? value.toLocaleString() : value}</div>
    </div>
  );
}

// ── Tab 2: Info ──

function InfoTab({ bp, isOwner, onUpdate }: { bp: Blueprint; isOwner: boolean; onUpdate: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(bp.name);
  const [description, setDescription] = useState(bp.description ?? "");
  const [requirement, setRequirement] = useState(bp.requirement_text ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setName(bp.name);
    setDescription(bp.description ?? "");
    setRequirement(bp.requirement_text ?? "");
  }, [bp]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      await api.patch(`/blueprints/${bp.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        requirement_text: requirement.trim() || null,
      });
      setEditing(false);
      onUpdate();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 720, paddingTop: 20 }}>
      {error && <Callout intent="danger" style={{ marginBottom: 16 }}>{error}</Callout>}
      <Card elevation={Elevation.ONE} style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h4 style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>Blueprint Details</h4>
          {isOwner && !editing && <Button size="small" icon="edit" onClick={() => setEditing(true)}>Edit</Button>}
          {editing && (
            <div style={{ display: "flex", gap: 6 }}>
              <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="small" intent="primary" loading={saving} onClick={handleSave}>Save</Button>
            </div>
          )}
        </div>

        {editing ? (
          <>
            <FormGroup label="Name">
              <InputGroup value={name} onChange={(e) => setName(e.target.value)} />
            </FormGroup>
            <FormGroup label="Description">
              <TextArea fill value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            </FormGroup>
            <FormGroup label="Targeting Requirements">
              <TextArea fill value={requirement} onChange={(e) => setRequirement(e.target.value)} rows={4} />
            </FormGroup>
          </>
        ) : (
          <div className="info-rows">
            <InfoRow label="Name" value={bp.name} />
            <InfoRow label="Description" value={bp.description || "—"} />
            <InfoRow label="Targeting Requirements" value={bp.requirement_text || "—"} />
            <InfoRow label="Status" value={bp.status.charAt(0).toUpperCase() + bp.status.slice(1)} />
            <InfoRow label="Leads Generated" value={String(bp.lead_count)} />
            <InfoRow label="Created" value={fmtDate(bp.created_at)} />
            <InfoRow label="Last Updated" value={fmtDate(bp.updated_at)} />
          </div>
        )}
      </Card>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-row">
      <span className="info-row-label">{label}</span>
      <span className="info-row-value">{value}</span>
    </div>
  );
}

// ── Tab 2: Leads ──

interface CompanyGroup {
  id: string | null;
  name: string;
  domain: string | null;
  leads: Lead[];
}

function groupLeadsByCompany(leads: Lead[]): CompanyGroup[] {
  const groups = new Map<string, CompanyGroup>();
  for (const l of leads) {
    const key = l.company_id || l.company_name || "__unassigned__";
    if (!groups.has(key)) {
      groups.set(key, { id: l.company_id || null, name: l.company_name || "Unknown Company", domain: l.company_domain || null, leads: [] });
    }
    groups.get(key)!.leads.push(l);
  }
  return Array.from(groups.values()).sort((a, b) => b.leads.length - a.leads.length);
}

const AVATAR_COLORS = ["#2b95d6", "#634dbf", "#0d8050", "#c87619", "#8f398f", "#1f6b6b", "#96290d", "#5642a6"];
function avatarColor(name: string) { let h = 0; for (const c of name) h = ((h << 5) - h + c.charCodeAt(0)) | 0; return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; }

const STATUS_TAG_MAP: Record<string, { intent: "success" | "primary" | "warning" | "danger" | "none"; label: string }> = {
  won: { intent: "success", label: "Won" },
  qualified: { intent: "success", label: "Qualified" },
  contacted: { intent: "primary", label: "Contacted" },
  claimed: { intent: "warning", label: "Claimed" },
  dead: { intent: "danger", label: "Dead" },
  lost: { intent: "danger", label: "Lost" },
};

function CompanyLeadRow({ group, blueprintId, blueprintName }: { group: CompanyGroup; blueprintId?: string; blueprintName?: string }) {
  const navigate = useNavigate();
  const { leads } = group;
  const bestScore = Math.max(...leads.map(l => l.score));
  const activeLeads = leads.filter(l => l.status !== "dead" && l.status !== "lost");
  const claimedCount = leads.filter(l => l.status !== "unclaimed").length;

  const statusCounts = leads.reduce<Record<string, number>>((acc, l) => {
    if (l.status !== "unclaimed") acc[l.status] = (acc[l.status] || 0) + 1;
    return acc;
  }, {});

  const handleClick = () => {
    if (!group.id) return;
    const params = new URLSearchParams();
    if (blueprintId) params.set("bp", blueprintId);
    const qs = params.toString();
    navigate(`/companies/${group.id}${qs ? `?${qs}` : ""}`, {
      state: { bpName: blueprintName },
    });
  };

  return (
    <tr className="company-row clickable" onClick={handleClick} style={{ cursor: group.id ? "pointer" : "default" }}>
      <td style={{ width: "30%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: avatarColor(group.name), display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
          }}>
            {group.name.charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.3 }}>{group.name}</div>
            {group.domain && <div style={{ fontSize: 11, color: "#5c7080", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.domain}</div>}
          </div>
        </div>
      </td>
      <td style={{ width: "15%" }}>
        <Tag minimal round style={{ fontSize: 11.5 }}>
          {activeLeads.length === leads.length
            ? `${leads.length} ${leads.length === 1 ? "contact" : "contacts"}`
            : `${activeLeads.length} active / ${leads.length}`
          }
        </Tag>
      </td>
      <td style={{ width: "20%" }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {Object.entries(statusCounts).map(([status, count]) => {
            const cfg = STATUS_TAG_MAP[status];
            if (!cfg) return null;
            return (
              <Tag key={status} minimal round intent={cfg.intent} style={{ fontSize: 10.5 }}>
                {count} {cfg.label}
              </Tag>
            );
          })}
        </div>
      </td>
      <td style={{ width: "10%" }}>
        <Tag minimal round intent={bestScore >= 80 ? "success" : bestScore >= 60 ? "warning" : "none"} style={{ fontWeight: 700 }}>
          {bestScore > 0 ? bestScore : "—"}
        </Tag>
      </td>
      <td style={{ width: "13%" }}>
        {claimedCount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: "#e1e8ed", overflow: "hidden" }}>
              <div style={{ width: `${(claimedCount / leads.length) * 100}%`, height: "100%", background: "#2b95d6", borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 11, color: "#5c7080" }}>{claimedCount}/{leads.length}</span>
          </div>
        ) : (
          <Tag minimal round style={{ fontSize: 11 }}>New</Tag>
        )}
      </td>
      <td style={{ width: "12%", textAlign: "right" }}>
        {group.id && <Icon icon="chevron-right" size={14} color="#8a9ba8" />}
      </td>
    </tr>
  );
}

function LeadsTab({ blueprintId, blueprintName, leadCount }: { blueprintId: string; blueprintName?: string; leadCount?: number }) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ blueprint_id: blueprintId, page: String(page), page_size: "100" });
    if (statusFilter === "__all__") {
      params.set("exclude_terminal", "false");
    } else if (statusFilter) {
      params.set("status", statusFilter);
      params.set("exclude_terminal", "false");
    }

    api.get<LeadListResponse>(`/leads/?${params}`)
      .then((r) => { setLeads(r.leads); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [blueprintId, page, statusFilter]);

  // Refetch on mount and whenever the parent reports a different lead_count
  // (e.g. the pipeline finished and produced new leads while the tab was open).
  useEffect(() => { fetchLeads(); }, [fetchLeads, leadCount]);

  const companyGroups = groupLeadsByCompany(leads);

  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <HTMLSelect minimal value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Active Leads</option>
          <option value="unclaimed">Unclaimed</option>
          <option value="claimed">Claimed</option>
          <option value="contacted">Contacted</option>
          <option value="qualified">Qualified</option>
          <option value="won">Won</option>
          <option value="dead">Dead</option>
          <option value="lost">Lost</option>
          <option value="__all__">All (incl. Dead/Lost)</option>
        </HTMLSelect>
        <span style={{ fontSize: 12, color: "#5c7080", marginLeft: "auto" }}>
          {companyGroups.length} {companyGroups.length === 1 ? "company" : "companies"} · {total} contacts
        </span>
      </div>

      <div className="dash-panel">
        <div className="dash-panel-body no-pad">
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={28} /></div>
          ) : companyGroups.length === 0 ? (
            <div style={{ padding: 40 }}><NonIdealState icon="office" title="No companies" description="Companies will appear here once generated." /></div>
          ) : (
            <table className="lead-table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Company</th>
                  <th style={{ width: "15%" }}>Contacts</th>
                  <th style={{ width: "20%" }}>Summary</th>
                  <th style={{ width: "10%" }}>Score</th>
                  <th style={{ width: "13%" }}>Progress</th>
                  <th style={{ width: "12%" }}></th>
                </tr>
              </thead>
              <tbody>
                {companyGroups.map(g => (
                  <CompanyLeadRow key={g.id || g.name} group={g} blueprintId={blueprintId} blueprintName={blueprintName} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {total > 100 && (
          <div style={{ padding: "12px 20px", borderTop: "1px solid #dce0e5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button disabled={page <= 1} icon="chevron-left" onClick={() => setPage(page - 1)} size="small">Prev</Button>
            <span style={{ fontSize: 12, color: "#5c7080" }}>Page {page} of {Math.ceil(total / 100)}</span>
            <Button disabled={page * 100 >= total} rightIcon="chevron-right" onClick={() => setPage(page + 1)} size="small">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 3: Members ──

function MembersTab({ bp, isOwner, onUpdate }: { bp: Blueprint; isOwner: boolean; onUpdate: () => void }) {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<UserItem[]>([]);
  const [addRole, setAddRole] = useState("collaborator");

  const [showInvite, setShowInvite] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState("collaborator");
  const [inviting, setInviting] = useState(false);
  const [invError, setInvError] = useState("");

  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(() => {
      api.get<UserItem[]>(`/auth/users/search?q=${encodeURIComponent(query)}`)
        .then((users) => {
          const memberIds = new Set(bp.members.map((m) => m.user_id));
          setSuggestions(users.filter((u) => !memberIds.has(u.id)));
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [query, bp.members]);

  const handleAddMember = async (user: UserItem) => {
    setError(""); setSuccess("");
    try {
      await api.post(`/blueprints/${bp.id}/members`, { email: user.email, role: addRole });
      setQuery("");
      setSuggestions([]);
      setSuccess(`${user.full_name} added as ${addRole}`);
      onUpdate();
    } catch (e: any) { setError(e.message); }
  };

  const handleRemove = async (member: BlueprintMember) => {
    setError("");
    try {
      await api.delete(`/blueprints/${bp.id}/members/${member.id}`);
      onUpdate();
    } catch (e: any) { setError(e.message); }
  };

  const handleRoleChange = async (member: BlueprintMember, newRole: string) => {
    setError("");
    try {
      await api.patch(`/blueprints/${bp.id}/members/${member.id}/role`, { role: newRole });
      onUpdate();
    } catch (e: any) { setError(e.message); }
  };

  const handleInvite = async () => {
    if (!invEmail.trim() || !invName.trim()) { setInvError("Email and name are required"); return; }
    setInviting(true); setInvError("");
    try {
      await api.post(`/blueprints/${bp.id}/invite`, { email: invEmail.trim(), full_name: invName.trim(), role: invRole });
      setShowInvite(false);
      setInvEmail(""); setInvName(""); setInvRole("collaborator");
      setSuccess(`Invite sent to ${invEmail.trim()}`);
      onUpdate();
    } catch (e: any) { setInvError(e.message); }
    finally { setInviting(false); }
  };

  return (
    <div style={{ paddingTop: 20 }}>
      {error && <Callout intent="danger" style={{ marginBottom: 12 }}>{error}</Callout>}
      {success && <Callout intent="success" icon="tick-circle" style={{ marginBottom: 12 }}>{success}</Callout>}

      {isOwner && (
        <Card elevation={Elevation.ONE} className="add-member-card">
          <h4 style={{ margin: "0 0 12px", fontWeight: 600, fontSize: 14 }}>Add Member</h4>
          <div className="add-member-row">
            <div className="add-member-suggest" style={{ position: "relative" }}>
              <Suggest<UserItem>
                items={suggestions}
                itemRenderer={(user, { handleClick, modifiers }) => (
                  <MenuItem
                    key={user.id}
                    text={user.full_name}
                    label={user.email}
                    onClick={handleClick}
                    active={modifiers.active}
                    roleStructure="listoption"
                  />
                )}
                onItemSelect={handleAddMember}
                query={query}
                onQueryChange={setQuery}
                inputValueRenderer={(u) => u.full_name}
                noResults={
                  query.length >= 2
                    ? <MenuItem disabled text="No matching users found" />
                    : <MenuItem disabled text="Type at least 2 characters to search..." />
                }
                popoverProps={{
                  minimal: true,
                  usePortal: false,
                  matchTargetWidth: true,
                  placement: "bottom-start",
                }}
                inputProps={{
                  placeholder: "Search by name or email...",
                  leftIcon: "search",
                }}
                resetOnSelect
              />
            </div>
            <HTMLSelect value={addRole} onChange={(e) => setAddRole(e.target.value)} style={{ width: 140 }}>
              <option value="collaborator">Collaborator</option>
              <option value="viewer">Viewer</option>
            </HTMLSelect>
            {isSuperadmin && (
              <Button icon="new-person" onClick={() => setShowInvite(true)}>Invite New</Button>
            )}
          </div>
        </Card>
      )}

      <Card elevation={Elevation.ONE} style={{ padding: 0 }}>
        <table className="lead-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Added</th>
              {isOwner && <th style={{ textAlign: "right" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {bp.members.map((m) => (
              <tr key={m.id}>
                <td style={{ fontWeight: 500 }}>{m.user_name ?? "—"}</td>
                <td style={{ fontSize: 13, color: "#5c7080" }}>{m.user_email ?? "—"}</td>
                <td>
                  {isOwner && m.role !== "owner" ? (
                    <HTMLSelect
                      minimal
                      value={m.role}
                      onChange={(e) => handleRoleChange(m, e.target.value)}
                      style={{ fontSize: 13 }}
                    >
                      <option value="collaborator">Collaborator</option>
                      <option value="viewer">Viewer</option>
                      <option value="owner">Owner</option>
                    </HTMLSelect>
                  ) : (
                    <Tag minimal round intent={m.role === "owner" ? "primary" : "none"}>
                      {m.role.charAt(0).toUpperCase() + m.role.slice(1)}
                    </Tag>
                  )}
                </td>
                <td style={{ fontSize: 12, color: "#5c7080" }}>{fmtDate(m.created_at)}</td>
                {isOwner && (
                  <td style={{ textAlign: "right" }}>
                    {m.role !== "owner" && (
                      <Button size="small" variant="minimal" icon="cross" intent="danger" onClick={() => handleRemove(m)} />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog isOpen={showInvite} onClose={() => setShowInvite(false)} title="Invite New User" style={{ width: 460 }}>
        <DialogBody>
          {invError && <Callout intent="danger" style={{ marginBottom: 16 }}>{invError}</Callout>}
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#5c7080" }}>
            This will create a new account and send login credentials via email. The user will automatically be added to this blueprint.
          </p>
          <FormGroup label="Full Name" labelInfo="(required)">
            <InputGroup placeholder="John Doe" value={invName} onChange={(e) => setInvName(e.target.value)} autoFocus />
          </FormGroup>
          <FormGroup label="Email" labelInfo="(required)">
            <InputGroup type="email" placeholder="john@company.com" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} />
          </FormGroup>
          <FormGroup label="Blueprint Role">
            <HTMLSelect fill value={invRole} onChange={(e) => setInvRole(e.target.value)}>
              <option value="collaborator">Collaborator</option>
              <option value="viewer">Viewer</option>
            </HTMLSelect>
          </FormGroup>
        </DialogBody>
        <DialogFooter actions={
          <>
            <Button onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button intent="primary" loading={inviting} onClick={handleInvite}>Create & Invite</Button>
          </>
        } />
      </Dialog>
    </div>
  );
}

